import { signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useNavigate } from "react-router-dom";
import auth from "../../firebase.init";
import axios from "axios";

const NavBar = () => {
  const [user, loading, error] = useAuthState(auth);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [footerData, setFooterData] = useState({
    logo: { src: "", alt: "" },
  });
  const [footerLoading, setFooterLoading] = useState(true);
  const [footerError, setFooterError] = useState(null);

  // Cache keys for localStorage
  const CACHE_KEYS = {
    USER_DATA: "navbar_user_data",
    FOOTER_DATA: "navbar_footer_data",
    AUTH_STATE: "navbar_auth_state",
  };

  // Load cached data on component mount
  useEffect(() => {
    const loadCachedData = () => {
      try {
        // Load cached user data
        const cachedUserData = localStorage.getItem(CACHE_KEYS.USER_DATA);
        if (cachedUserData) {
          setUserData(JSON.parse(cachedUserData));
        }
        // Load cached footer data
        const cachedFooterData = localStorage.getItem(CACHE_KEYS.FOOTER_DATA);
        if (cachedFooterData) {
          setFooterData(JSON.parse(cachedFooterData));
          setFooterLoading(false);
        }
        // Load cached auth state
        const cachedAuthState = localStorage.getItem(CACHE_KEYS.AUTH_STATE);
        if (cachedAuthState) {
          const authData = JSON.parse(cachedAuthState);
          // Only use cached auth state if it's recent (less than 5 minutes old)
          if (Date.now() - authData.timestamp < 5 * 60 * 1000) {
            setAuthLoading(false);
          }
        }
      } catch (error) {
        console.error("Error loading cached data:", error);
      }
    };
    loadCachedData();
  }, []);

  // Handle Firebase auth state changes
  useEffect(() => {
    if (!loading) {
      setAuthLoading(false);
      // Cache auth state
      try {
        const authStateData = {
          hasUser: !!user,
          timestamp: Date.now(),
          userEmail: user?.email || null,
        };
        localStorage.setItem(
          CACHE_KEYS.AUTH_STATE,
          JSON.stringify(authStateData)
        );
      } catch (error) {
        console.error("Error caching auth state:", error);
      }
    }
  }, [user, loading]);

  // Fetch footer data with caching
  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        setFooterLoading(true);
        const response = await axios.get(
          "https://octoberfour-60f2c682a1d1.herokuapp.com/api/footer-section"
        );
        if (response.data.success) {
          // Transform API data to match our component structure
          const apiData = response.data.data;
          const transformedData = {
            ...apiData,
            logo: {
              src: apiData.middleSection?.logoUrl || "",
              alt: apiData.topSection?.logoText || "Aithm",
            },
          };

          setFooterData(transformedData);

          // Cache footer data
          try {
            localStorage.setItem(
              CACHE_KEYS.FOOTER_DATA,
              JSON.stringify(transformedData)
            );
            localStorage.setItem(
              "footer_data_timestamp",
              Date.now().toString()
            );
          } catch (error) {
            console.error("Error caching footer data:", error);
          }
        } else {
          setFooterError("Failed to fetch footer data");
        }
      } catch (err) {
        console.error("Error fetching footer data:", err);
        setFooterError("Error fetching footer data");
      } finally {
        setFooterLoading(false);
      }
    };

    // Only fetch if we don't have cached data or if it's been more than 1 hour
    const cachedFooterData = localStorage.getItem(CACHE_KEYS.FOOTER_DATA);
    const lastFetchTime = localStorage.getItem("footer_data_timestamp") || "0";
    const shouldFetch =
      !cachedFooterData ||
      Date.now() - parseInt(lastFetchTime) > 60 * 60 * 1000;

    if (shouldFetch) {
      fetchFooterData();
    } else {
      // If we have cached data, make sure it has the correct structure
      try {
        const parsedData = JSON.parse(cachedFooterData);
        if (!parsedData.logo) {
          // Transform cached data if it doesn't have the correct structure
          const transformedData = {
            ...parsedData,
            logo: {
              src: parsedData.middleSection?.logoUrl || "",
              alt: parsedData.topSection?.logoText || "Aithm",
            },
          };
          setFooterData(transformedData);
          localStorage.setItem(
            CACHE_KEYS.FOOTER_DATA,
            JSON.stringify(transformedData)
          );
        }
      } catch (error) {
        console.error("Error transforming cached footer data:", error);
        fetchFooterData();
      }
    }
  }, []);

  // Fetch user data with caching
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.email) {
        setUserData(null);
        // Clear cached user data when no user
        try {
          localStorage.removeItem(CACHE_KEYS.USER_DATA);
        } catch (error) {
          console.error("Error clearing cached user data:", error);
        }
        return;
      }
      try {
        const response = await fetch(
          `https://octoberfour-60f2c682a1d1.herokuapp.com/users?userEmail=${user.email}`
        );
        const data = await response.json();
        if (data && data.length > 0) {
          const userData = data[0];
          setUserData(userData);
          // Cache user data
          try {
            localStorage.setItem(
              CACHE_KEYS.USER_DATA,
              JSON.stringify(userData)
            );
          } catch (error) {
            console.error("Error caching user data:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    // Only fetch user data if we have a user and either no cached data or cached data is stale
    if (user?.email) {
      const cachedUserData = localStorage.getItem(CACHE_KEYS.USER_DATA);
      const shouldFetch = !cachedUserData || !userData;
      if (shouldFetch) {
        fetchUserData();
      }
    }
  }, [user]);

  const handleSignout = async () => {
    try {
      await signOut(auth);
      // Clear all cached data on logout
      Object.values(CACHE_KEYS).forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error(`Error clearing ${key} from localStorage:`, error);
        }
      });
      setUserData(null);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const toggleUserDropdown = () => {
    setUserDropdownOpen(!userDropdownOpen);
  };

  const closeUserDropdown = () => {
    setUserDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDropdownOpen &&
        !event.target.closest(".user-dropdown-container")
      ) {
        closeUserDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userDropdownOpen]);

  // Get logo source with fallback
  const getLogoSrc = () => {
    if (footerLoading) return "";
    if (footerError) return "";
    return footerData?.logo?.src || "";
  };

  // Get logo alt text with fallback
  const getLogoAlt = () => {
    if (footerLoading) return "";
    if (footerError) return "";
    return footerData?.logo?.alt;
  };




  return (




    // <> <header className="main-header">
    //   {/*Header-Upper*/}
    //   <div className="header-upper">
    //     <div className="container-fluid clearfix">
    //       <div className="header-inner rel d-flex align-items-center">
    //         <div className="logo-outer">
    // <div className="logo"><a href="/">
    //   {footerLoading ? (
    //     <span></span>
    //   ) : footerError ? (
    //     <span>Logo</span>
    //   ) : (
    //     <img src={getLogoSrc()} alt={getLogoAlt()} />
    //   )}
    // </a></div>
    //         </div>
    //         <div className="nav-outer clearfix">
    //           {/* Main Menu */}
    //           <nav className="main-menu navbar-expand-lg">
    //             <div className="navbar-header">
    //               <div className="mobile-logo my-15">
    //                 <a href="/">
    //                   {footerLoading ? (
    //                     <span></span>
    //                   ) : footerError ? (
    //                     <span>Logo</span>
    //                   ) : (
    //                     <img src={getLogoSrc()} alt={getLogoAlt()} />
    //                   )}
    //                 </a>
    //               </div>
    //               {/* Toggle Button */}
    //               <button type="button" className="navbar-toggle" data-bs-toggle="collapse" data-bs-target=".navbar-collapse">
    //                 <span className="icon-bar" />
    //                 <span className="icon-bar" />
    //                 <span className="icon-bar" />
    //               </button>
    //             </div>
    //             <div className="navbar-collapse collapse clearfix">
    //               <ul className="navigation onepage clearfix ">
    //                 {/* <li><a href="#home">Home</a></li>
    //                 <li><a href="#about-sec">About</a></li>
    //                 <li><a href="#pricing-sec">Pricing</a></li>
    //                 <li><a href="#testimonials-sec">Testimonials</a></li> */}

    //     <div className="auth-section mt-3 d-lg-none">
    //     {authLoading ? (
    //       <div className="auth-loading">Loading...</div>
    //     ) : user ? (
    //       <div className="user-dropdown-container dropdown">
    //         <button
    //           className="btn btn-light w-100 d-flex justify-content-between align-items-center shadow-sm rounded px-3 py-2"
    //           onClick={toggleUserDropdown}
    //         >
    //           <div className="d-flex align-items-center gap-2">
    //             {userData?.profileURL ? (
    //               <img
    //                 src={userData.profileURL}
    //                 alt={userData?.userName || "User"}
    //                 className="rounded-circle"
    //                 style={{
    //                   width: "36px",
    //                   height: "36px",
    //                   objectFit: "cover",
    //                 }}
    //               />
    //             ) : (
    //               <div
    //                 className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center"
    //                 style={{ width: "36px", height: "36px" }}
    //               >
    //                 <i className="bx bx-user"></i>
    //               </div>
    //             )}
    //             <span className="fw-semibold">
    //               {userData?.userName || user.displayName || "User"}
    //             </span>
    //           </div>
    //           <i
    //             className={`bx bx-chevron-${userDropdownOpen ? "up" : "down"
    //               }`}
    //           ></i>
    //         </button>

    //         {userDropdownOpen && (
    //           <div className="dropdown-menu w-100 mt-2 shadow-sm border rounded show">
    //             <Link
    //               className="dropdown-item d-flex align-items-center gap-2"
    //               to="/user/dashboard"
    //               onClick={closeUserDropdown}
    //             >
    //               <i className="bx bx-home"></i> Dashboard
    //             </Link>

    //             {userData?.userRole === "Admin" && (
    //               <Link
    //                 className="dropdown-item d-flex align-items-center gap-2"
    //                 to="/admin/dashboard"
    //                 onClick={closeUserDropdown}
    //               >
    //                 <i className="bx bx-shield-quarter"></i> Admin
    //                 Panel
    //               </Link>
    //             )}

    //             {/* {userData?.currentBalance !== undefined && (
    //               <div className="dropdown-item d-flex align-items-center gap-2">
    //                 <i className="bx bx-wallet"></i>
    //                 <span>Credits: {userData.currentBalance}</span>
    //               </div>
    //             )} */}

    //             <div className="dropdown-divider"></div>

    //             <button
    //               className="dropdown-item d-flex align-items-center gap-2 logout-btn"
    //               onClick={handleSignout}
    //             >
    //               <i className="bx bx-log-out"></i> Logout
    //             </button>
    //           </div>
    //         )}
    //       </div>
    //     ) : (
    //       <div className="text-black">
    //         {/* <a
    //           href="/login"
    //           className="login mt-3 m-2"
    //         >
    //           Login
    //         </a> */}
    //         <a
    //           href="/register"
    //           className="theme-btn text-black me-2 m-2 mt-2"
    //         >
    //           My Account
    //           <i className="fas fa-angle-double-right" />
    //         </a>
    //       </div>

    //     )}
    //   </div>
    //   </ul>
    // </div>
    //           </nav>
    //           {/* Main Menu End*/}
    //         </div>
    //         {/* Menu Button */}
    //         <div className="menu-btns mb-5">

    //    <div className="auth-section mt-3 d-none d-md-block mb-5">
    //       {authLoading ? (
    //         <div className="auth-loading">Loading...</div>
    //       ) : user ? (
    //         <div className="user-dropdown-container dropdown">
    //           <button
    //             className="btn btn-light w-100 d-flex justify-content-between align-items-center shadow-sm rounded px-3 py-2"
    //             onClick={toggleUserDropdown}
    //           >
    //             <div className="d-flex align-items-center gap-2">
    //               {userData?.profileURL ? (
    //                 <img
    //                   src={userData.profileURL}
    //                   alt={userData?.userName || "User"}
    //                   className="rounded-circle"
    //                   style={{
    //                     width: "36px",
    //                     height: "36px",
    //                     objectFit: "cover",
    //                   }}
    //                 />
    //               ) : (
    //                 <div
    //                   className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center"
    //                   style={{ width: "36px", height: "36px" }}
    //                 >
    //                   <i className="bx bx-user"></i>
    //                 </div>
    //               )}
    //               <span className="fw-semibold">
    //                 {userData?.userName || user.displayName || "User"}
    //               </span>
    //             </div>
    //             <i
    //               className={`bx bx-chevron-${userDropdownOpen ? "up" : "down"}`}
    //             ></i>
    //           </button>

    //           {userDropdownOpen && (
    //             <div className="dropdown-menu w-100 mt-2 shadow-sm border rounded show">
    //               <Link
    //                 className="dropdown-item d-flex align-items-center gap-2"
    //                 to="/user/dashboard"
    //                 onClick={closeUserDropdown}
    //               >
    //                 <i className="bx bx-home"></i> Dashboard
    //               </Link>

    //               {userData?.userRole === "Admin" && (
    //                 <Link
    //                   className="dropdown-item d-flex align-items-center gap-2"
    //                   to="/admin/dashboard"
    //                   onClick={closeUserDropdown}
    //                 >
    //                   <i className="bx bx-shield-quarter"></i> Admin Panel
    //                 </Link>
    //               )}

    //               {/* {userData?.currentBalance !== undefined && (
    //                 <div className="dropdown-item d-flex align-items-center gap-2">
    //                   <i className="bx bx-wallet"></i>
    //                   <span>Credits: {userData.currentBalance}</span>
    //                 </div>
    //               )} */}

    //               <div className="dropdown-divider"></div>

    //               <button
    //                 className="dropdown-item d-flex align-items-center gap-2 logout-btn"
    //                 onClick={handleSignout}
    //               >
    //                 <i className="bx bx-log-out"></i> Logout
    //               </button>
    //             </div>
    //           )}
    //         </div>
    //       ) : (
    //         <div className="d-none d-md-flex">
    //           {/* <Link
    //             to="/login"
    //             className="login mt-3 m-2"
    //           >
    //             Login
    //           </Link> */}
    //           <Link
    //             to="/register"
    //             className="theme-btn text-black me-2 m-2"

    //           >

    //              My Account
    //              <i className="fas fa-angle-double-right" />
    //           </Link>
    //         </div>
    //       )}
    //     </div>

    // </div>
    //       </div>
    //     </div>
    //   </div>
    //   {/*End Header Upper*/}
    // </header>
    // </>

    <><header className="cs_site_header cs_style_1 cs_sticky_header">
      <div className="cs_main_header cs_fs_18 cs_heading_color">
        <div className="container">
          <div className="cs_main_header_in">
            <div className="cs_main_header_left">

              <a className="cs_site_branding" href="/">
                {footerLoading ? (
                  <span></span>
                ) : footerError ? (
                  <span>Logo</span>
                ) : (
                  <img src={getLogoSrc()} alt={getLogoAlt()} />
                )}
              </a>
            </div>
            <div className="cs_main_header_center">
              <div className="cs_nav">
                <ul className="cs_nav_list">

                  <div className="auth-section mt-3 d-none d-md-block mb-5">
                    {authLoading ? (
                      <div className="auth-loading">Loading...</div>
                    ) : user ? (
                      <div className="user-dropdown-container dropdown">
                        <button
                          className="btn btn-light w-100 d-flex justify-content-between align-items-center shadow-sm rounded px-3 py-2"
                          onClick={toggleUserDropdown}
                        >
                          <div className="d-flex align-items-center gap-2">
                            {userData?.profileURL ? (
                              <img
                                src={userData.profileURL}
                                alt={userData?.userName || "User"}
                                className="rounded-circle"
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: "36px", height: "36px" }}
                              >
                                <i className="bx bx-user"></i>
                              </div>
                            )}
                            <span className="fw-semibold">
                              {userData?.userName || user.displayName || "User"}
                            </span>
                          </div>
                          <i
                            className={`bx bx-chevron-${userDropdownOpen ? "up" : "down"}`}
                          ></i>
                        </button>

                        {userDropdownOpen && (
                          <div className="dropdown-menu w-100 mt-2 shadow-sm border rounded show">
                            <Link
                              className="dropdown-item d-flex align-items-center gap-2"
                              to="/user/dashboard"
                              onClick={closeUserDropdown}
                            >
                              <i className="bx bx-home"></i> Dashboard
                            </Link>

                            {userData?.userRole === "Admin" && (
                              <Link
                                className="dropdown-item d-flex align-items-center gap-2"
                                to="/admin/dashboard"
                                onClick={closeUserDropdown}
                              >
                                <i className="bx bx-shield-quarter"></i> Admin Panel
                              </Link>
                            )}

                            {/* {userData?.currentBalance !== undefined && (
                            <div className="dropdown-item d-flex align-items-center gap-2">
                              <i className="bx bx-wallet"></i>
                              <span>Credits: {userData.currentBalance}</span>
                            </div>
                          )} */}

                            <div className="dropdown-divider"></div>

                            <button
                              className="dropdown-item d-flex align-items-center gap-2 logout-btn"
                              onClick={handleSignout}
                            >
                              <i className="bx bx-log-out"></i> Logout
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="d-none d-md-flex ">
                        {/* <Link
                        to="/login"
                        className="login mt-3 m-2"
                      >
                        Login
                      </Link> */}
                        <Link
                          to="/register"
                          className="cs_custom_btn "
                        >
                          My Account
                        </Link>

                      </div>
                    )}
                  </div>


                  <div className="auth-section m-3 d-lg-none ">
                    {authLoading ? (
                      <div className="auth-loading">Loading...</div>
                    ) : user ? (
                      <div className="user-dropdown-container dropdown">
                        <button
                          className="btn btn-light btn-sm d-flex justify-content-between align-items-center shadow-sm rounded px-3 py-2"
                          onClick={toggleUserDropdown}
                          style={{ minWidth: "120px" }} // optional, ensures small but readable
                        >
                          <div className="d-flex align-items-center gap-2">
                            {userData?.profileURL ? (
                              <img
                                src={userData.profileURL}
                                alt={userData?.userName || "User"}
                                className="rounded-circle"
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: "36px", height: "36px" }}
                              >
                                <i className="bx bx-user"></i>
                              </div>
                            )}
                            <span className="fw-semibold">
                              {userData?.userName || user.displayName || "User"}
                            </span>
                          </div>
                          <i
                            className={`bx bx-chevron-${userDropdownOpen ? "up" : "down"}`}
                          ></i>
                        </button>

                        {userDropdownOpen && (
                          <div className="dropdown-menu mt-2 shadow-sm border rounded show">
                            <Link
                              className="dropdown-item d-flex align-items-center gap-2"
                              to="/user/dashboard"
                              onClick={closeUserDropdown}
                            >
                              <i className="bx bx-home"></i> Dashboard
                            </Link>

                            {userData?.userRole === "Admin" && (
                              <Link
                                className="dropdown-item d-flex align-items-center gap-2"
                                to="/admin/dashboard"
                                onClick={closeUserDropdown}
                              >
                                <i className="bx bx-shield-quarter"></i> Admin Panel
                              </Link>
                            )}

                            <div className="dropdown-divider"></div>

                            <button
                              className="dropdown-item d-flex align-items-center gap-2 logout-btn"
                              onClick={handleSignout}
                            >
                              <i className="bx bx-log-out"></i> Logout
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <a
                          href="/register"
                          className="cs_custom_btn btn btn-sm m-3"
                          style={{ minWidth: "20px" }} // optional for small button
                        >
                          My Account
                        </a>
                      </div>
                    )}
                  </div>


                  <div class="cs_btn_group position-relative">

                  </div>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
    </>


  );
};
export default NavBar;
